import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Package, ShoppingCart, Users, BarChart3, Plus, Edit, Trash2, X, Upload, Image, Search, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import Navbar from '@/components/Navbar';
import { accountsAPI, adminAPI, categoriesAPI, uploadAPI } from '@/utils/api';
import { toast } from 'sonner';

const AdminDashboard = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-8">
            Admin Dashboard
          </h1>
          
          <div className="flex gap-4 mb-8 border-b border-white/10 overflow-x-auto" data-testid="admin-tabs">
            <Link to="/admin">
              <Button variant={useLocation().pathname === '/admin' ? 'default' : 'ghost'} className="rounded-t-lg rounded-b-none" data-testid="analytics-tab">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
            </Link>
            <Link to="/admin/accounts">
              <Button variant={useLocation().pathname === '/admin/accounts' ? 'default' : 'ghost'} className="rounded-t-lg rounded-b-none" data-testid="accounts-tab">
                <Package className="w-4 h-4 mr-2" />
                Accounts
              </Button>
            </Link>
            <Link to="/admin/categories">
              <Button variant={useLocation().pathname === '/admin/categories' ? 'default' : 'ghost'} className="rounded-t-lg rounded-b-none" data-testid="categories-tab">
                <Package className="w-4 h-4 mr-2" />
                Categories
              </Button>
            </Link>
            <Link to="/admin/orders">
              <Button variant={useLocation().pathname === '/admin/orders' ? 'default' : 'ghost'} className="rounded-t-lg rounded-b-none" data-testid="orders-tab">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Orders
              </Button>
            </Link>
            <Link to="/admin/customers">
              <Button variant={useLocation().pathname === '/admin/customers' ? 'default' : 'ghost'} className="rounded-t-lg rounded-b-none" data-testid="customers-tab">
                <Users className="w-4 h-4 mr-2" />
                Customers
              </Button>
            </Link>
            <Link to="/admin/admins">
              <Button variant={useLocation().pathname === '/admin/admins' ? 'default' : 'ghost'} className="rounded-t-lg rounded-b-none" data-testid="admins-tab">
                <Users className="w-4 h-4 mr-2" />
                Admins
              </Button>
            </Link>
            <Link to="/admin/coupons">
              <Button variant={useLocation().pathname === '/admin/coupons' ? 'default' : 'ghost'} className="rounded-t-lg rounded-b-none" data-testid="coupons-tab">
                <Package className="w-4 h-4 mr-2" />
                Coupons
              </Button>
            </Link>
            <Link to="/admin/tracking">
              <Button variant={useLocation().pathname === '/admin/tracking' ? 'default' : 'ghost'} className="rounded-t-lg rounded-b-none" data-testid="tracking-tab">
                <Users className="w-4 h-4 mr-2" />
                User Tracking
              </Button>
            </Link>
          </div>
          
          <Routes>
            <Route path="/" element={<Analytics />} />
            <Route path="/accounts" element={<AccountsManagement />} />
            <Route path="/categories" element={<CategoriesManagement />} />
            <Route path="/orders" element={<OrdersManagement />} />
            <Route path="/customers" element={<CustomersManagement />} />
            <Route path="/admins" element={<AdminsManagement />} />
            <Route path="/coupons" element={<CouponsManagement />} />
            <Route path="/tracking" element={<UserTrackingManagement />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await adminAPI.getAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="analytics-section">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="total-revenue-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-bold">Total Revenue</h3>
            <BarChart3 className="w-5 h-5 text-primary" />
          </div>
          <p className="text-3xl font-heading font-black" data-testid="total-revenue">₹{analytics?.total_revenue || 0}</p>
        </div>
        
        <div className="p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="total-orders-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-bold">Total Orders</h3>
            <ShoppingCart className="w-5 h-5 text-accent" />
          </div>
          <p className="text-3xl font-heading font-black" data-testid="total-orders">{analytics?.total_orders || 0}</p>
        </div>
        
        <div className="p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="total-customers-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-bold">Total Customers</h3>
            <Users className="w-5 h-5 text-primary" />
          </div>
          <p className="text-3xl font-heading font-black" data-testid="total-customers">{analytics?.total_customers || 0}</p>
        </div>
        
        <div className="p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="total-accounts-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-bold">Total Accounts</h3>
            <Package className="w-5 h-5 text-accent" />
          </div>
          <p className="text-3xl font-heading font-black" data-testid="total-accounts">{analytics?.total_accounts || 0}</p>
        </div>
      </div>
    </div>
  );
};

const AccountsManagement = () => {
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    category_id: '',
    title: '',
    description: '',
    price: '',
    credentials: '',
    stock: '',
    image_url: '',
    featured: false,
    validity_days: '30'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [accountsRes, categoriesRes] = await Promise.all([
        accountsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setAccounts(accountsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      // Construct full URL for the image
      const fullUrl = response.data.url;
      setFormData({ ...formData, image_url: fullUrl });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock),
      validity_days: parseInt(formData.validity_days)
    };

    try {
      if (editingAccount) {
        await adminAPI.updateAccount(editingAccount.account_id, data);
        toast.success('Account updated');
      } else {
        await adminAPI.createAccount(data);
        toast.success('Account created');
      }
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account');
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      category_id: account.category_id,
      title: account.title,
      description: account.description,
      price: account.price.toString(),
      credentials: account.credentials || '',
      stock: account.stock.toString(),
      image_url: account.image_url,
      featured: account.featured,
      validity_days: account.validity_days?.toString() || '30'
    });
    setShowDialog(true);
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    
    try {
      await adminAPI.deleteAccount(accountId);
      toast.success('Account deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const resetForm = () => {
    setEditingAccount(null);
    setFormData({
      category_id: '',
      title: '',
      description: '',
      price: '',
      credentials: '',
      stock: '',
      image_url: '',
      featured: false,
      validity_days: '30'
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="accounts-management-section">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-heading font-bold">Manage Accounts</h2>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary-glow rounded-full" data-testid="add-account-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Account
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#121214] border border-white/10">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">
                {editingAccount ? 'Edit Account' : 'Add New Account'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="account-form">
              <div>
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  required
                  data-testid="category-select"
                  className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white mt-2"
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="title-input"
                  className="bg-white/5 border-white/10 mt-2"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="description-input"
                  className="bg-white/5 border-white/10 mt-2"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Price (₹)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    data-testid="price-input"
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="stock">Stock</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    required
                    data-testid="stock-input"
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="validity_days">Validity (Days)</Label>
                <Input
                  id="validity_days"
                  type="number"
                  value={formData.validity_days}
                  onChange={(e) => setFormData({ ...formData, validity_days: e.target.value })}
                  required
                  data-testid="validity-input"
                  className="bg-white/5 border-white/10 mt-2"
                  placeholder="30"
                />
              </div>

              <div>
                <Label htmlFor="credentials">Credentials</Label>
                <Textarea
                  id="credentials"
                  value={formData.credentials}
                  onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
                  required
                  data-testid="credentials-input"
                  className="bg-white/5 border-white/10 mt-2"
                  rows={2}
                  placeholder="Email: user@example.com | Password: pass123"
                />
              </div>

              <div>
                <Label>Product Image</Label>
                <div className="mt-2 space-y-3">
                  {/* File Upload */}
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      id="image-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-2"
                      data-testid="upload-image-button"
                    >
                      <Upload className="w-4 h-4" />
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Max 5MB (JPG, PNG, GIF, WebP)
                    </span>
                  </div>
                  
                  {/* Divider */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-white/10"></div>
                    <span className="text-xs text-muted-foreground">or paste URL</span>
                    <div className="flex-1 h-px bg-white/10"></div>
                  </div>
                  
                  {/* URL Input */}
                  <Input
                    id="image_url"
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    required
                    data-testid="image-url-input"
                    className="bg-white/5 border-white/10"
                    placeholder="https://example.com/image.jpg"
                  />
                  
                  {/* Image Preview */}
                  {formData.image_url && (
                    <div className="mt-2 p-2 rounded-lg border border-white/10 bg-white/5">
                      <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="max-h-32 rounded-lg object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  data-testid="featured-checkbox"
                  className="w-4 h-4"
                />
                <Label htmlFor="featured">Featured Account</Label>
              </div>

              <Button type="submit" className="w-full btn-primary-glow rounded-full" data-testid="submit-account-button">
                {editingAccount ? 'Update Account' : 'Create Account'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="accounts-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Title</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Price</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Stock</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Featured</th>
              <th className="text-right p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.account_id} className="border-b border-white/5" data-testid={`account-row-${account.account_id}`}>
                <td className="p-4">{account.title}</td>
                <td className="p-4">₹{account.price}</td>
                <td className="p-4">{account.stock}</td>
                <td className="p-4">{account.featured ? 'Yes' : 'No'}</td>
                <td className="p-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(account)}
                    data-testid={`edit-account-${account.account_id}`}
                    className="mr-2"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(account.account_id)}
                    data-testid={`delete-account-${account.account_id}`}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await adminAPI.getAllOrders();
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="orders-management-section">
      <h2 className="text-2xl font-heading font-bold mb-6">All Orders</h2>
      
      <div className="space-y-4">
        {orders.map((order) => (
          <div
            key={order.order_id}
            className="p-6 rounded-2xl border border-white/10 bg-white/5"
            data-testid={`admin-order-${order.order_id}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-heading font-bold text-lg mb-1">Order #{order.order_id}</h3>
                <p className="text-sm text-muted-foreground">
                  {new Date(order.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold ${
                  order.status === 'completed'
                    ? 'bg-accent/10 text-accent'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {order.status.toUpperCase()}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">User ID</p>
                <p className="font-mono text-sm">{order.user_id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Delivery Email</p>
                <p className="text-sm">{order.delivery_email}</p>
              </div>
            </div>
            
            <div className="border-t border-white/10 pt-4">
              <p className="text-sm text-muted-foreground mb-2">Items:</p>
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between text-sm mb-1">
                  <span>{item.title} (x{item.quantity})</span>
                  <span className="font-heading font-bold">₹{item.price * item.quantity}</span>
                </div>
              ))}
              <div className="flex justify-between mt-2 pt-2 border-t border-white/10">
                <span className="font-heading font-bold">Total</span>
                <span className="text-xl font-heading font-black">₹{order.total_amount}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CustomersManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await adminAPI.getCustomers();
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="customers-management-section">
      <h2 className="text-2xl font-heading font-bold mb-6">All Customers</h2>
      
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="customers-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Name</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Email</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">User ID</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Joined</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.user_id} className="border-b border-white/5" data-testid={`customer-row-${customer.user_id}`}>
                <td className="p-4">{customer.name}</td>
                <td className="p-4">{customer.email}</td>
                <td className="p-4 font-mono text-sm">{customer.user_id}</td>
                <td className="p-4">
                  {new Date(customer.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image_url: ''
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await adminAPI.updateCategory(editingCategory.category_id, formData);
        toast.success('Category updated');
      } else {
        await adminAPI.createCategory(formData);
        toast.success('Category created');
      }
      setShowDialog(false);
      resetForm();
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description,
      image_url: category.image_url
    });
    setShowDialog(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await adminAPI.deleteCategory(categoryId);
      toast.success('Category deleted');
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      image_url: ''
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="categories-management-section">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-heading font-bold">Manage Categories</h2>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary-glow rounded-full" data-testid="add-category-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-[#121214] border border-white/10">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="category-form">
              <div>
                <Label htmlFor="name">Category Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({ 
                      ...formData, 
                      name,
                      slug: name.toLowerCase().replace(/\s+/g, '-')
                    });
                  }}
                  required
                  data-testid="category-name-input"
                  className="bg-white/5 border-white/10 mt-2"
                />
              </div>

              <div>
                <Label htmlFor="slug">URL Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  data-testid="category-slug-input"
                  className="bg-white/5 border-white/10 mt-2"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="category-description-input"
                  className="bg-white/5 border-white/10 mt-2"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  data-testid="category-image-input"
                  className="bg-white/5 border-white/10 mt-2"
                />
              </div>

              <Button type="submit" className="w-full btn-primary-glow rounded-full" data-testid="submit-category-button">
                {editingCategory ? 'Update Category' : 'Create Category'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((category) => (
          <div
            key={category.category_id}
            className="p-6 rounded-2xl border border-white/10 bg-white/5"
            data-testid={`category-card-${category.category_id}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-heading font-bold">{category.name}</h3>
                <p className="text-sm text-muted-foreground">/{category.slug}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(category)}
                  data-testid={`edit-category-${category.category_id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(category.category_id)}
                  data-testid={`delete-category-${category.category_id}`}
                  className="text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground text-sm">{category.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminsManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const response = await adminAPI.getAllAdmins();
      setAdmins(response.data);
    } catch (error) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createAdmin(formData);
      toast.success('Admin created successfully');
      setShowDialog(false);
      setFormData({ email: '', password: '', name: '' });
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="admins-management-section">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-heading font-bold">Admin Users</h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary-glow rounded-full" data-testid="add-admin-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Admin
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-[#121214] border border-white/10">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">Create New Admin</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-form">
              <div>
                <Label htmlFor="admin-name">Full Name</Label>
                <Input
                  id="admin-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="admin-name-input"
                  className="bg-white/5 border-white/10 mt-2"
                />
              </div>

              <div>
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="admin-email-input"
                  className="bg-white/5 border-white/10 mt-2"
                />
              </div>

              <div>
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="admin-password-input"
                  className="bg-white/5 border-white/10 mt-2"
                />
              </div>

              <Button type="submit" className="w-full btn-primary-glow rounded-full" data-testid="submit-admin-button">
                Create Admin
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="admins-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Name</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Email</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">User ID</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Created</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => (
              <tr key={admin.user_id} className="border-b border-white/5" data-testid={`admin-row-${admin.user_id}`}>
                <td className="p-4">{admin.name}</td>
                <td className="p-4">{admin.email}</td>
                <td className="p-4 font-mono text-sm">{admin.user_id}</td>
                <td className="p-4">
                  {new Date(admin.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CouponsManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    min_purchase_amount: '0'
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const response = await adminAPI.getAllCoupons();
      setCoupons(response.data);
    } catch (error) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createCoupon({
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        max_uses: parseInt(formData.max_uses),
        min_purchase_amount: parseFloat(formData.min_purchase_amount)
      });
      toast.success('Coupon created successfully');
      setShowDialog(false);
      resetForm();
      loadCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    
    try {
      await adminAPI.deleteCoupon(couponId);
      toast.success('Coupon deleted');
      loadCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      max_uses: '',
      valid_from: '',
      valid_until: '',
      min_purchase_amount: '0'
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="coupons-management-section">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-heading font-bold">Manage Coupons</h2>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary-glow rounded-full" data-testid="add-coupon-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Coupon
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-[#121214] border border-white/10 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">Create New Coupon</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="coupon-form">
              <div>
                <Label htmlFor="code">Coupon Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  data-testid="code-input"
                  className="bg-white/5 border-white/10 mt-2"
                  placeholder="SAVE20"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="discount_type">Discount Type</Label>
                  <select
                    id="discount_type"
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    required
                    className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white mt-2"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="discount_value">
                    {formData.discount_type === 'percentage' ? 'Discount (%)' : 'Amount (₹)'}
                  </Label>
                  <Input
                    id="discount_value"
                    type="number"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    required
                    data-testid="discount-value-input"
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="max_uses">Max Uses</Label>
                  <Input
                    id="max_uses"
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    required
                    data-testid="max-uses-input"
                    className="bg-white/5 border-white/10 mt-2"
                    placeholder="100"
                  />
                </div>

                <div>
                  <Label htmlFor="min_purchase">Min Purchase (₹)</Label>
                  <Input
                    id="min_purchase"
                    type="number"
                    step="0.01"
                    value={formData.min_purchase_amount}
                    onChange={(e) => setFormData({ ...formData, min_purchase_amount: e.target.value })}
                    data-testid="min-purchase-input"
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valid_from">Valid From</Label>
                  <Input
                    id="valid_from"
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    required
                    data-testid="valid-from-input"
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="valid_until">Valid Until</Label>
                  <Input
                    id="valid_until"
                    type="datetime-local"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    required
                    data-testid="valid-until-input"
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full btn-primary-glow rounded-full" data-testid="submit-coupon-button">
                Create Coupon
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="coupons-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Code</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Discount</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Uses</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Valid Until</th>
              <th className="text-right p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((coupon) => (
              <tr key={coupon.coupon_id} className="border-b border-white/5" data-testid={`coupon-row-${coupon.coupon_id}`}>
                <td className="p-4 font-mono font-bold text-primary">{coupon.code}</td>
                <td className="p-4">
                  {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}
                </td>
                <td className="p-4">{coupon.used_count} / {coupon.max_uses}</td>
                <td className="p-4">
                  {new Date(coupon.valid_until).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
                <td className="p-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(coupon.coupon_id)}
                    data-testid={`delete-coupon-${coupon.coupon_id}`}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const UserTrackingManagement = () => {
  const [tracking, setTracking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTracking, setEditingTracking] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    user_id: '',
    order_id: '',
    account_purchased: '',
    account_credentials: '',
    purchase_date: '',
    expiry_date: '',
    replacement_taken: false,
    user_email: '',
    user_contact: ''
  });

  useEffect(() => {
    loadTracking();
  }, []);

  const loadTracking = async () => {
    try {
      const response = await adminAPI.getAllTracking();
      setTracking(response.data);
    } catch (error) {
      toast.error('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      loadTracking();
      return;
    }
    
    try {
      const response = await adminAPI.searchTracking(query);
      setTracking(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTracking) {
        await adminAPI.updateTracking(editingTracking.tracking_id, formData);
        toast.success('Tracking updated');
      } else {
        await adminAPI.createTracking(formData);
        toast.success('Tracking created');
      }
      setShowDialog(false);
      resetForm();
      loadTracking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tracking');
    }
  };

  const handleEdit = (track) => {
    setEditingTracking(track);
    setFormData({
      user_id: track.user_id,
      order_id: track.order_id,
      account_purchased: track.account_purchased,
      account_credentials: track.account_credentials,
      purchase_date: track.purchase_date,
      expiry_date: track.expiry_date,
      replacement_taken: track.replacement_taken,
      user_email: track.user_email,
      user_contact: track.user_contact || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (trackingId) => {
    if (!window.confirm('Are you sure you want to delete this tracking record?')) return;
    
    try {
      await adminAPI.deleteTracking(trackingId);
      toast.success('Tracking deleted');
      loadTracking();
    } catch (error) {
      toast.error('Failed to delete tracking');
    }
  };

  const resetForm = () => {
    setEditingTracking(null);
    setFormData({
      user_id: '',
      order_id: '',
      account_purchased: '',
      account_credentials: '',
      purchase_date: '',
      expiry_date: '',
      replacement_taken: false,
      user_email: '',
      user_contact: ''
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="tracking-management-section">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h2 className="text-2xl font-heading font-bold">User Tracking</h2>
        <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
          {/* Search Box */}
          <div className="relative flex-1 md:w-64">
            <Input
              type="text"
              placeholder="Search by email, order, user..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="bg-white/5 border-white/10 pl-10"
              data-testid="tracking-search-input"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary-glow rounded-full" data-testid="add-tracking-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Record
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl bg-[#121214] border border-white/10 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">
                {editingTracking ? 'Edit Tracking' : 'Add Tracking Record'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="tracking-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="user_id">User ID</Label>
                  <Input
                    id="user_id"
                    value={formData.user_id}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="order_id">Order ID</Label>
                  <Input
                    id="order_id"
                    value={formData.order_id}
                    onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="account_purchased">Account Purchased</Label>
                <Input
                  id="account_purchased"
                  value={formData.account_purchased}
                  onChange={(e) => setFormData({ ...formData, account_purchased: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 mt-2"
                  placeholder="Netflix Premium - 4K"
                />
              </div>

              <div>
                <Label htmlFor="account_credentials">Account Credentials</Label>
                <Textarea
                  id="account_credentials"
                  value={formData.account_credentials}
                  onChange={(e) => setFormData({ ...formData, account_credentials: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 mt-2"
                  rows={2}
                  placeholder="Email: user@example.com | Password: pass123"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="purchase_date">Purchase Date</Label>
                  <Input
                    id="purchase_date"
                    type="datetime-local"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="expiry_date">Expiry Date</Label>
                  <Input
                    id="expiry_date"
                    type="datetime-local"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="user_email">User Email</Label>
                  <Input
                    id="user_email"
                    type="email"
                    value={formData.user_email}
                    onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="user_contact">User Contact (Optional)</Label>
                  <Input
                    id="user_contact"
                    value={formData.user_contact}
                    onChange={(e) => setFormData({ ...formData, user_contact: e.target.value })}
                    className="bg-white/5 border-white/10 mt-2"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="replacement_taken"
                  checked={formData.replacement_taken}
                  onChange={(e) => setFormData({ ...formData, replacement_taken: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="replacement_taken">Replacement Taken</Label>
              </div>

              <Button type="submit" className="w-full btn-primary-glow rounded-full" data-testid="submit-tracking-button">
                {editingTracking ? 'Update Record' : 'Add Record'}
              </Button>
            </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="tracking-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Account</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">User Email</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Purchase Date</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Expiry</th>
              <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Replacement</th>
              <th className="text-right p-4 text-xs uppercase tracking-wider text-muted-foreground font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tracking.map((track) => (
              <tr key={track.tracking_id} className="border-b border-white/5" data-testid={`tracking-row-${track.tracking_id}`}>
                <td className="p-4">{track.account_purchased}</td>
                <td className="p-4">{track.user_email}</td>
                <td className="p-4">
                  {new Date(track.purchase_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
                <td className="p-4">
                  {new Date(track.expiry_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
                <td className="p-4">
                  {track.replacement_taken ? (
                    <span className="text-accent">✓ Yes</span>
                  ) : (
                    <span className="text-muted-foreground">No</span>
                  )}
                </td>
                <td className="p-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(track)}
                    className="mr-2"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(track.tracking_id)}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;
